import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Box, Tooltip } from '@mui/material';
import { CSSProperties, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { PC } from '../paymentConsolidation.classes';
import { hasPaymentConsolidationCustomerId, resolveScrTranslatedLabel } from '../paymentConsolidation.mapper';

interface IProps {
    props: any;
}

const cellOverflowStyle: CSSProperties = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
};

const renderOverflowCell = (value: unknown) => {
    const displayValue = value != null && value !== '' && value !== '-' ? String(value) : '-';

    return (
        <Tooltip title={displayValue}>
            <div style={cellOverflowStyle}>
                <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayValue}
                </p>
            </div>
        </Tooltip>
    );
};

const renderScrCell = (
    customerId: number | null | undefined,
    isScr: boolean,
    yesLabel: string,
    noLabel: string
) => {
    const displayValue = resolveScrTranslatedLabel(customerId, isScr, {
        yes: yesLabel,
        no: noLabel,
    });

    if (!hasPaymentConsolidationCustomerId(customerId)) {
        return renderOverflowCell(displayValue);
    }

    return (
        <Tooltip title={displayValue}>
            <div style={{ ...cellOverflowStyle, textAlign: 'center' }}>
                <p
                    style={{
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isScr ? '#d32f2f' : undefined,
                        fontWeight: isScr ? 600 : undefined,
                    }}
                >
                    {displayValue}
                </p>
            </div>
        </Tooltip>
    );
};

const renderLinkCell = (href: string, value: unknown) => {
    const displayValue = value != null && value !== '' ? String(value) : '-';

    return (
        <Tooltip title={displayValue}>
            <div style={cellOverflowStyle}>
                <a target="_blank" className="linktxt" href={href} rel="noreferrer" style={cellOverflowStyle}>
                    {displayValue}
                </a>
            </div>
        </Tooltip>
    );
};

const dataGridSx = {
    width: '100%',
    '& .MuiDataGrid-main': {
        overflowX: 'auto',
    },
    '& .MuiDataGrid-cell': {
        overflow: 'hidden',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

export const PaymentConsolidationDataGrid: FC<IProps> = ({ props }) => {
    const { t } = useTranslation();
    const excelDownloadFolderName = t('general.bulkShipmentSearchPage');
    const columns: ExtendedGridColDef[] = [
        {
            field: 'orderNumber',
            headerName: t('general.orderNumber'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) =>
                renderLinkCell('/orderdetail?OrderNumber=' + params.row.orderNumber, params.row.orderNumber),
        },
        {
            field: 'shipmentNumber',
            headerName: t('general.shippingNo'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) =>
                renderLinkCell('/orderdetail?shippingListNo=' + params.row.shippingListNo, params.row.shippingListNo),
        },
        {
            field: 'orderDateStr',
            headerName: t('general.orderDate'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.orderDateStr),
        },
        {
            field: 'shippedDateStr',
            headerName: t('general.shippedDate'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.shippedDateStr),
        },
        {
            field: 'cargoCompanyName',
            headerName: t('general.cargoCompanyName'),
            flex: 1,
            minWidth: 130,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.cargoCompanyName),
        },
        {
            field: 'shippingListStatusName',
            headerName: t('general.shippingListStatusName'),
            flex: 1,
            minWidth: 170,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => {
                return renderOverflowCell(params.row.shippingListStatusName);
            },
        },
        {
            field: 'cityName',
            headerName: t('general.cityName'),
            flex: 1,
            minWidth: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.cityName),
        },
        {
            field: 'countyName',
            headerName: t('general.countyName'),
            flex: 1,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.countyName),
        },
        {
            field: 'deliveryMethodName',
            headerName: t('paymentConsolidation.deliveryMethodDistribution'),
            flex: 1,
            minWidth: 170,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.deliveryMethodName),
        },
        {
            field: 'shipmentOwner',
            headerName: t('paymentConsolidation.orderSource'),
            flex: 1,
            minWidth: 130,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.shipmentOwner),
        },
        {
            field: 'paymentAmount',
            headerName: t('refund.paymentAmount'),
            flex: 1,
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.paymentAmount),
        },
        {
            field: 'paymentMethodId',
            headerName: t('general.paymentType'),
            flex: 1,
            minWidth: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => renderOverflowCell(params.row.paymentMethodName),
        },
        {
            field: 'isScr',
            headerName: t('customerInformation.scr'),
            flex: 1,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) =>
                renderScrCell(params.row.customerId, params.row.isScr === true, t('general.yes'), t('general.no')),
        },
    ];

    return (
        <Box className={PC.dataGridSection} sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
            <SCUICustomDataGrid
                randomId
                rows={props.rows || []}
                columns={columns}
                sx={dataGridSx}
                onPageSizeChange={(i: number) => {
                    props.setPageSize(i);
                }}
                pageSize={props.pageSize}
                paginationModeChange
                onPageChange={(i: number) => {
                    props.setPageNumber(i);
                }}
                page={props.pageNumber}
                rowCount={props.rowCount}
                excelDownloadButton={true}
                excelDownloadData={props.excelProps}
                excelDownloadFolderName={excelDownloadFolderName}
                hiddenExcelColumn={props.hiddenExcelColumn}
            />
        </Box>
    );
};
